import { defineRepeaterCustomElement } from './fluid-columns-repeater'
import style from './style/FluidColumnsRepeater.scss'
import type { ComponentType, MouseEventHandler } from 'react'
import React, { useLayoutEffect, useRef } from 'react'
import _ from 'lodash'
import type { IFluidColumnsRepeater, FluidColumnsRepeaterCompProps } from './types'
import type { CellsMapType, IdIndexMapType } from '@wix/thunderbolt-commons'
import { supportedKeyboardKeys, getRepeaterCells, getNextCell } from '@wix/thunderbolt-commons'
import semanticClassNames from '@wix/editor-elements-semantic-classnames/dist/libs/semanticClassNames.json'
import { formatClassNames } from '@wix/editor-elements-common-utils'
import { FluidColumnsRepeaterController } from './FluidColumnsRepeater.controller'

const repeaterSemanticClassName = semanticClassNames.Repeater.Repeater.root || ''
let didDefineRepeaterCustomElement = false
const useEffect = process.env.browser ? useLayoutEffect : () => {}

const FluidColumnsRepeater: ComponentType<FluidColumnsRepeaterCompProps> = ({
	id,
	children,
	items,
	horizontalGap = 0,
	verticalGap = 0,
	justifyContent = 'start',
	onMouseEnter = _.noop,
	onMouseLeave = _.noop,
	a11y = {},
	keyboardNavigationEnabled,
	className,
	customClassNames = [],
	observeChildListChange,
	direction,
	isSeo,
}) => {
	if (process.env.browser && !didDefineRepeaterCustomElement) {
		defineRepeaterCustomElement(window)
		didDefineRepeaterCustomElement = true
	}
	const { label, role } = a11y

	const wrapperId = `${id}_wrapper`
	const ref = useRef<IFluidColumnsRepeater | null>(null)

	const [rootWidth, setRootWidth] = React.useState<number>(0)
	const [cellsRefs, setCellsRefs] = React.useState<CellsMapType>([])
	const [indexMap, setIndexMap] = React.useState<IdIndexMapType>({})

	useEffect(() => {
		if (ref.current && keyboardNavigationEnabled) {
			setRootWidth(ref.current.offsetWidth)
			const { cellsMap, idIndexMap } = getRepeaterCells(items, ref.current)
			cellsMap.forEach((row, rowIndex) =>
				row.forEach((cell, columnIndex) => {
					cell.setAttribute('tabindex', rowIndex === 0 && columnIndex === 0 ? '0' : '-1')
				})
			)
			setCellsRefs(cellsMap)
			setIndexMap(idIndexMap)
		}
	}, [keyboardNavigationEnabled, items])

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (!keyboardNavigationEnabled || !ref.current) {
			return
		}

		const target = event.target as Element
		let cellsMap = cellsRefs
		let idIndexMap = indexMap

		const shouldHandleKeyboardEvent =
			supportedKeyboardKeys.includes(event.key) && target.tagName !== 'INPUT' && target.tagName !== 'SELECT'

		if (!shouldHandleKeyboardEvent || !idIndexMap[target.id]) {
			return
		}

		event.preventDefault()

		if (ref.current.offsetWidth !== rootWidth) {
			const repeaterCells = getRepeaterCells(items, ref.current)
			cellsMap = repeaterCells.cellsMap
			idIndexMap = repeaterCells.idIndexMap
			setCellsRefs(repeaterCells.cellsMap)
			setIndexMap(repeaterCells.idIndexMap)
			setRootWidth(ref.current.offsetWidth)
		}

		const cellRef: HTMLElement | undefined = getNextCell(cellsMap, idIndexMap, target.id, event.key)

		cellRef?.focus()
	}

	React.useEffect(() => {
		if (observeChildListChange && ref.current) {
			observeChildListChange(id, ref.current)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div
			id={id}
			onMouseEnter={onMouseEnter as MouseEventHandler}
			onMouseLeave={onMouseLeave as MouseEventHandler}
			className={[className, formatClassNames(repeaterSemanticClassName, ...customClassNames)].join(' ')}
		>
			{/* dom-id-usage-check-ignore-next-line -- skin part id (compId + _wrapper) passed as container-id, never carries an identity class */}
			<div className={style.wrapper} id={wrapperId}>
				<fluid-columns-repeater
					style={isSeo ? undefined : { visibility: 'hidden' }}
					ref={ref}
					horizontal-gap={horizontalGap}
					vertical-gap={verticalGap}
					justify-content={justifyContent}
					container-id={wrapperId}
					items={items.length}
					class={style.repeater}
					role={role}
					aria-label={label}
					onKeyDown={handleKeyDown}
					direction={direction}
				>
					{items.map((itemId, index) =>
						children({
							parentType: 'Repeater',
							scopeId: itemId,
							itemIndex: index,
						})
					)}
				</fluid-columns-repeater>
			</div>
		</div>
	)
}

export const component = FluidColumnsRepeater

export const controller = FluidColumnsRepeaterController
